import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { setToken, wipeToken } from "@/lib/session";
import { clearClientHoverCache } from "@/components/ClientHoverCard";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch (err) {
      // 401 is expected when no session — only log unexpected errors
      if (err?.response?.status && err.response.status !== 401) {
        console.error("auth/me failed:", err);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.warn("Logout request failed (continuing local logout):", err);
    }
    localStorage.removeItem("pb_session_token"); // legacy key sweep
    wipeToken();
    clearClientHoverCache(); // clear any cached 360° client data on shared browsers
    setUser(null);
  };

  const setAuthFromResponse = (data) => {
    setUser(data.user);
    if (data.session_token) setToken(data.session_token);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout, setAuthFromResponse }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
